import { useCallback } from 'react';
import { doc, updateDoc, setDoc, deleteDoc, getDoc, getDocs, collection, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { FullProject, Task, LogEntry, ProjectMetadata } from '../../types';

export interface LibraryProject {
  id: string;
  name: string;
  color?: string;
  workspace?: 'professional' | 'private';
  description?: string;
  sharedBy?: string;
  sharedAt?: number;
  tasks?: Task[];
  metadata?: ProjectMetadata;
  isGlobal?: boolean;
  sharedWith?: string[];
}

export interface AuthUserLike {
  uid?: string;
  email?: string | null;
  displayName?: string | null;
}

interface UseProjectCollaborationProps {
  allProjects: FullProject[];
  setAllProjects: React.Dispatch<React.SetStateAction<FullProject[]>>;
  addLogEntry: (action: LogEntry['action'], description: string) => void;
  user: AuthUserLike | null;
  userId?: string | null;
  syncProjectToFirestore: (project: FullProject, isBackground?: boolean) => Promise<unknown>;
  setCurrentProjectId: (id: string) => void;
}

export function useProjectCollaboration({
  allProjects,
  setAllProjects,
  addLogEntry,
  user,
  userId,
  syncProjectToFirestore,
  setCurrentProjectId
}: UseProjectCollaborationProps) {

  const shareProjectWithUser = useCallback(async (
    projectId: string, 
    email: string, 
    role: 'viewer' | 'editor' = 'editor'
  ) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) throw new Error("Projekt nicht gefunden");
    
    const emailLower = email.trim().toLowerCase();
    if (!emailLower) throw new Error("Ungültige E-Mail-Adresse");

    const currentShared = project.sharedWith || [];
    const currentSharedEditors = project.sharedEditors || [];
    
    const updatedShared = currentShared.includes(emailLower) 
      ? currentShared 
      : [...currentShared, emailLower];
      
    let updatedEditors = [...currentSharedEditors];
    if (role === 'editor') {
      if (!updatedEditors.includes(emailLower)) {
        updatedEditors.push(emailLower);
      }
    } else {
      updatedEditors = updatedEditors.filter(e => e !== emailLower);
    }
    
    const currentInvitations = project.invitations || {};
    const safeKey = emailLower.replace(/\./g, '_dot_');
    const updatedInvitations = {
      ...currentInvitations,
      [safeKey]: {
        email: emailLower,
        role: role,
        invitedAt: Date.now(),
        status: 'pending'
      }
    };

    // Update local state first
    setAllProjects(prev => prev.map(p => p.id === projectId ? { 
      ...p, 
      sharedWith: updatedShared, 
      sharedEditors: updatedEditors,
      invitations: updatedInvitations
    } : p));
    
    // Sync to Firestore
    const docRef = doc(db, 'projects', projectId);
    await updateDoc(docRef, {
      sharedWith: updatedShared,
      sharedEditors: updatedEditors,
      invitations: updatedInvitations
    });
    
    addLogEntry('share', `Projekt für ${emailLower} als ${role === 'editor' ? 'Mitarbeiter' : 'Leser'} freigegeben`);
  }, [allProjects, addLogEntry, setAllProjects]);

  const unshareProjectWithUser = useCallback(async (projectId: string, email: string) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) throw new Error("Projekt nicht gefunden");
    
    const emailLower = email.trim().toLowerCase();
    const currentShared = project.sharedWith || [];
    const currentSharedEditors = project.sharedEditors || [];
    
    const updatedShared = currentShared.filter(e => e !== emailLower);
    const updatedEditors = currentSharedEditors.filter(e => e !== emailLower);

    const currentInvitations = { ...(project.invitations || {}) };
    const safeKey = emailLower.replace(/\./g, '_dot_');
    delete currentInvitations[safeKey];

    // Update local state first
    setAllProjects(prev => prev.map(p => p.id === projectId ? { 
      ...p, 
      sharedWith: updatedShared, 
      sharedEditors: updatedEditors,
      invitations: currentInvitations
    } : p));
    
    // Sync to Firestore
    const docRef = doc(db, 'projects', projectId);
    await updateDoc(docRef, {
      sharedWith: updatedShared,
      sharedEditors: updatedEditors,
      invitations: currentInvitations
    });
    
    addLogEntry('unshare', `Freigabe für ${emailLower} aufgehoben`);
  }, [allProjects, addLogEntry, setAllProjects]);

  const updateProjectShareRole = useCallback(async (projectId: string, email: string, role: 'viewer' | 'editor') => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) throw new Error("Projekt nicht gefunden");
    
    const emailLower = email.trim().toLowerCase();
    const currentShared = project.sharedWith || [];
    const currentSharedEditors = project.sharedEditors || [];
    
    // Ensure email is in sharedWith
    const updatedShared = currentShared.includes(emailLower)
      ? currentShared
      : [...currentShared, emailLower];
      
    let updatedEditors = [...currentSharedEditors];
    if (role === 'editor') {
      if (!updatedEditors.includes(emailLower)) {
        updatedEditors.push(emailLower);
      }
    } else {
      updatedEditors = updatedEditors.filter(e => e !== emailLower);
    }

    const currentInvitations = { ...(project.invitations || {}) };
    const safeKey = emailLower.replace(/\./g, '_dot_');
    if (currentInvitations[safeKey]) {
      currentInvitations[safeKey] = {
        ...currentInvitations[safeKey],
        role: role
      };
    }

    // Update local state
    setAllProjects(prev => prev.map(p => p.id === projectId ? { 
      ...p, 
      sharedWith: updatedShared, 
      sharedEditors: updatedEditors,
      invitations: currentInvitations
    } : p));
    
    // Sync to Firestore
    const docRef = doc(db, 'projects', projectId);
    await updateDoc(docRef, {
      sharedWith: updatedShared,
      sharedEditors: updatedEditors,
      invitations: currentInvitations
    });
    
    addLogEntry('share', `Berechtigung für ${emailLower} auf ${role === 'editor' ? 'Mitarbeiter' : 'Leser'} geändert`);
  }, [allProjects, addLogEntry, setAllProjects]);

  const publishProjectToLibrary = useCallback(async (
    projectId: string, 
    description: string, 
    visibility: 'global' | 'private' | 'emails' = 'global',
    sharedWithEmails: string[] = []
  ) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) throw new Error("Projekt nicht gefunden");

    // Filter tasks and remove contact fields
    const strippedTasks = (project.tasks || [])
      .filter(t => t.type !== 'contact')
      .map(t => {
        const {
          assignee,
          organization,
          contactEmail,
          contactPhone,
          lastContactDate,
          role,
          contactFunction,
          contactChecklist,
          ...restTask
        } = t;
        return {
          ...restTask,
          notes: typeof restTask.notes === 'string' 
            ? restTask.notes 
            : (restTask.notes || []).map(n => ({ id: n.id, text: n.text, timestamp: n.timestamp, author: n.author || '' }))
        };
      });

    // Remove stakeholders from metadata
    const cleanMetadata = {
      ...(project.metadata || {}),
      stakeholders: []
    };

    const libraryDocRef = doc(db, 'project_library', projectId);
    const libraryData = {
      id: projectId,
      name: project.name,
      color: project.color || 'bg-blue-500',
      workspace: project.workspace || 'professional',
      description: description || '',
      sharedBy: user?.email?.toLowerCase() || user?.uid || 'unknown',
      sharedAt: Date.now(),
      tasks: strippedTasks,
      metadata: cleanMetadata,
      isGlobal: visibility === 'global',
      sharedWith: visibility === 'emails' ? sharedWithEmails.map(e => e.trim().toLowerCase()) : []
    };

    await setDoc(libraryDocRef, libraryData);
  }, [allProjects, user?.email, user?.uid]);

  const importProjectFromLibrary = useCallback(async (
    libraryProjectId: string, 
    targetWorkspace: 'professional' | 'private'
  ) => {
    const docRef = doc(db, 'project_library', libraryProjectId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error("Vorbelegung in Bibliothek nicht gefunden");
    }

    const libData = docSnap.data() as Partial<LibraryProject>;
    const newId = Date.now().toString();
    
    // Create new project metadata
    const newProject: FullProject = {
      id: newId,
      name: libData.name || "Importiertes Projekt",
      color: libData.color || "bg-blue-500",
      workspace: targetWorkspace,
      userId: userId || 'unknown',
      lastModified: Date.now(),
      metadata: libData.metadata || {},
      changelog: [
        {
          id: Date.now().toString(),
          timestamp: new Date().toISOString(),
          action: 'import',
          description: 'Projekt aus der Bibliothek importiert'
        }
      ],
      tasks: []
    };

    const tasksToImport = libData.tasks || [];
    const idMap = new Map<string | number, string | number>();
    
    // 1st pass: Generate new IDs and map them
    tasksToImport.forEach((t: Task) => {
      const newTaskId = Math.random().toString(36).substring(2, 9);
      idMap.set(t.id, newTaskId);
    });

    // 2nd pass: Map fields
    const newTasks = tasksToImport.map((t: Task) => {
      const newTaskId = idMap.get(t.id)!;
      const mappedTask: Task = {
        ...t,
        id: newTaskId,
        projectId: newId,
        userId: userId || 'unknown',
        parentId: t.parentId ? (idMap.get(t.parentId) || t.parentId) : undefined,
        dependencies: (t.dependencies || []).map((dep: string | number) => idMap.get(dep) || dep)
      };
      return mappedTask;
    });

    newProject.tasks = newTasks;

    // Save project
    await syncProjectToFirestore(newProject);
    setAllProjects(prev => {
      if (prev.some(p => p.id === newId)) return prev;
      return [...prev, newProject];
    });
    
    setCurrentProjectId(newId);
    return newProject;
  }, [userId, syncProjectToFirestore, setAllProjects, setCurrentProjectId]);

  const deleteProjectFromLibrary = useCallback(async (libraryProjectId: string) => {
    const docRef = doc(db, 'project_library', libraryProjectId);
    await deleteDoc(docRef);
  }, []);

  const fetchLibraryProjects = useCallback(async () => {
    const q = query(collection(db, 'project_library'), orderBy('sharedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const allTemplates: LibraryProject[] = querySnapshot.docs.map(doc => ({
      ...(doc.data() as Partial<LibraryProject>),
      id: doc.id
    } as LibraryProject));

    const userEmail = user?.email?.toLowerCase() || '';
    return allTemplates.filter(item => {
      // Show if global
      if (item.isGlobal === true || item.isGlobal === undefined) return true;
      // Show if published by self
      if (item.sharedBy?.toLowerCase() === userEmail) return true;
      // Show if shared with this user's email
      if (item.sharedWith && item.sharedWith.includes(userEmail)) return true;
      return false;
    });
  }, [user]);

  return {
    shareProjectWithUser,
    unshareProjectWithUser,
    updateProjectShareRole,
    publishProjectToLibrary,
    importProjectFromLibrary,
    deleteProjectFromLibrary,
    fetchLibraryProjects
  };
}
